import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sparkles, TrendingUp, Calendar, ShoppingCart, ArrowRight } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import ProductCard from '../../molecules/ProductCard/ProductCard';

const AIRecommendations = () => {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('personalized');
  const [recommendations, setRecommendations] = useState({
    personalized: [],
    seasonal: [],
    trending: [],
  });
  const [loading, setLoading] = useState(true);

  const mockData = {
    personalized: [
      { id: 101, name: 'Basmati Rice Premium', brand: 'India Gate', price: 12.99, weight: '5kg', image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400', rating: 4.9, reviews: 567, tag: 'Recommended', reason: 'Based on your purchases' },
      { id: 102, name: 'Turmeric Powder', brand: 'Aashirvaad', price: 3.49, weight: '200g', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400', rating: 4.7, reviews: 234, tag: 'Recommended', reason: 'Brands you like' },
      { id: 103, name: 'Coconut Oil', brand: 'Parachute', price: 4.99, weight: '500ml', image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400', rating: 4.8, reviews: 345, tag: 'Recommended', reason: 'Customers also bought' },
      { id: 104, name: 'Sambar Powder', brand: 'MTR', price: 2.99, weight: '100g', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400', rating: 4.6, reviews: 189, tag: 'Recommended', reason: 'Based on your purchases' },
    ],
    seasonal: [
      { id: 201, name: 'Mango Pickle', brand: "Mother's Recipe", price: 3.99, weight: '300g', image: 'https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=400', rating: 4.7, reviews: 456, tag: 'Summer Special', reason: 'Seasonal picks' },
      { id: 202, name: 'Rose Sherbet', brand: 'Rooh Afza', price: 5.49, weight: '750ml', image: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400', rating: 4.8, reviews: 234, tag: 'Summer Special', reason: 'Seasonal picks' },
      { id: 203, name: 'Curd/Yogurt', brand: 'Amul', price: 2.49, weight: '400g', image: 'https://images.unsplash.com/photo-1566385101042-1a0aa0c1268c?w=400', rating: 4.6, reviews: 678, tag: 'Fresh', reason: 'Seasonal picks' },
      { id: 204, name: 'Coconut Water', brand: 'Tender', price: 1.99, weight: '200ml', image: 'https://images.unsplash.com/photo-1566385101042-1a0aa0c1268c?w=400', rating: 4.5, reviews: 123, tag: 'Summer Special', reason: 'Seasonal picks' },
    ],
    trending: [
      { id: 301, name: 'Garam Masala', brand: 'Everest', price: 2.99, weight: '100g', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400', rating: 4.9, reviews: 890, tag: 'Trending', reason: 'Popular now' },
      { id: 302, name: 'Instant Dosa Mix', brand: 'MTR', price: 3.49, weight: '500g', image: 'https://images.unsplash.com/photo-1627485937980-221c88ac04f9?w=400', rating: 4.7, reviews: 567, tag: 'Trending', reason: 'Popular now' },
      { id: 303, name: 'Filter Coffee', brand: 'Levista', price: 6.99, weight: '200g', image: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400', rating: 4.8, reviews: 432, tag: 'Trending', reason: 'Popular now' },
      { id: 304, name: 'Paneer', brand: 'Amul', price: 3.99, weight: '200g', image: 'https://images.unsplash.com/photo-1566385101042-1a0aa0c1268c?w=400', rating: 4.6, reviews: 789, tag: 'Fresh', reason: 'Popular now' },
    ],
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setRecommendations(mockData);
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const tabs = [
    { id: 'personalized', label: 'For You', icon: Sparkles, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { id: 'seasonal', label: 'Seasonal', icon: Calendar, color: 'text-green-600', bgColor: 'bg-green-50' },
    { id: 'trending', label: 'Trending', icon: TrendingUp, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  ];

  const currentData = recommendations[activeTab] || [];
  const currentTab = tabs.find(t => t.id === activeTab);

  if (loading) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4 mx-auto" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-12 mx-auto" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl h-80" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-orange-100 rounded-full text-sm font-semibold mb-4">
            <Sparkles size={16} className="text-purple-600" />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-orange-600">
              AI-Powered Recommendations
            </span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Curated Just for <span className="gradient-text">You</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Our intelligent recommendation engine analyzes your preferences, purchase history, and seasonal trends to suggest products you will love.
          </p>
        </motion.div>

        <div className="flex justify-center gap-3 mb-12">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                activeTab === tab.id
                  ? `${tab.bgColor} ${tab.color} shadow-md ring-2 ring-offset-2 ring-purple-200`
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={18} />
              {tab.label}
            </motion.button>
          ))}
        </div>

        <motion.div
          className="text-center mb-8"
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${currentTab?.bgColor} ${currentTab?.color}`}>
            <currentTab.icon size={16} />
            <span className="font-medium">
              {activeTab === 'personalized' && isAuthenticated
                ? 'Based on your purchase history and preferences'
                : activeTab === 'personalized'
                ? 'Sign in to see personalized recommendations'
                : activeTab === 'seasonal'
                ? 'Perfect picks for this time of year'
                : 'What our community is buying right now'}
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {currentData.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="group relative">
                <div className={`absolute top-3 left-3 z-10 px-3 py-1 rounded-full text-xs font-bold text-white shadow-md ${
                  activeTab === 'personalized' ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
                  activeTab === 'seasonal' ? 'bg-gradient-to-r from-green-500 to-teal-500' :
                  'bg-gradient-to-r from-orange-500 to-red-500'
                }`}>
                  <div className="flex items-center gap-1">
                    <Sparkles size={12} />
                    {product.tag}
                  </div>
                </div>
                <div className="absolute top-3 right-3 z-10 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-medium text-gray-600 shadow-sm">
                  {product.reason}
                </div>
                <ProductCard product={product} />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/shop">
            <motion.button
              className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:border-primary-500 hover:text-primary-600 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Explore All Products <ArrowRight size={18} />
            </motion.button>
          </Link>
        </div>

        <motion.div
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          {[
            { icon: Sparkles, label: 'AI-Powered', value: 'Smart matching', desc: 'Machine learning algorithms analyze your preferences' },
            { icon: TrendingUp, label: 'Real-time', value: 'Live trends', desc: 'Recommendations update based on community activity' },
            { icon: ShoppingCart, label: 'Personalized', value: 'Just for you', desc: 'Unique suggestions based on your history' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-xl flex items-center justify-center">
                <stat.icon size={24} className="text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default AIRecommendations;
